import * as React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
    createBattleAgent,
    getBattleAgent,
    getBattleAgentBotAccounts,
    getBattleAgentRuns,
    getCompetitionOptions,
    getUserTags,
    searchBattleAgentCreators,
    updateBattleAgent,
} from '../Api';
import type {
    BattleAgentBotAccountDto,
    BattleAgentCreatorSort,
    BattleAgentDto,
    BattleAgentInviteTagMode,
    BattleAgentMatchPopularityCriterion,
    BattleAgentMatchSortBy,
    BattleAgentMaxActiveVisibility,
    BattleAgentMode,
    BattleAgentOddsStrategy,
    BattleAgentStakeMode,
    BattleAgentRunDto,
    BattleAgentUpsertRequest,
} from '../models/dtos/BattleAgentDto';
import { emptyBattleAgentForm } from '../models/dtos/BattleAgentDto';
import UserTagDefinitionDto from '../models/dtos/UserTagDefinitionDto';
import CompetitionOptions from '../models/dtos/CompetitionOptions';

type CandidateUser = {
    id: number;
    name: string | null;
    email: string | null;
    tags: string[];
    lastActiveAt: string | null;
    moneyBalance: number | null;
};

const ODDS_STRATEGIES: BattleAgentOddsStrategy[] = ['RANDOM', 'FAVORITE', 'UNDERDOG'];
const STAKE_MODES: BattleAgentStakeMode[] = ['RANGE', 'TARGET_AVERAGE'];
const MATCH_POPULARITY_OPTIONS: { value: BattleAgentMatchPopularityCriterion; label: string }[] = [
    { value: 'BETS', label: 'Bets placed' },
    { value: 'VIEWS', label: 'Views' },
    { value: 'INTEREST', label: 'Interest (league + teams)' },
];
const MATCH_SORT_OPTIONS: BattleAgentMatchSortBy[] = ['RANDOM', 'BETS', 'VIEWS', 'INTEREST'];
const STAKE_ROUNDING_OPTIONS: { value: number; label: string }[] = [
    { value: 0, label: 'No rounding' },
    { value: 100, label: 'Nearest 100' },
    { value: 500, label: 'Nearest 500' },
    { value: 1000, label: 'Nearest 1000' },
];
const MAX_ACTIVE_VISIBILITY_OPTIONS: { value: BattleAgentMaxActiveVisibility; label: string }[] = [
    { value: 'BOTH', label: 'Both (public + private)' },
    { value: 'PUBLIC', label: 'Public only' },
    { value: 'PRIVATE', label: 'Private only' },
];
const SORT_OPTIONS: BattleAgentCreatorSort[] = [
    'LAST_ACTIVE_ASC',
    'LAST_ACTIVE_DESC',
    'BALANCE_DESC',
    'ACCOUNT_AGE_ASC',
    'ACCOUNT_AGE_DESC',
];

const toUpsert = (agent: BattleAgentDto): BattleAgentUpsertRequest => ({
    slug: agent.slug,
    name: agent.name,
    enabled: agent.enabled,
    globalKillSwitch: agent.globalKillSwitch,
    creatorUserIds: agent.creatorUserIds,
    modes: agent.modes,
    publicShare: agent.publicShare,
    stakeMin: agent.stakeMin,
    stakeMax: agent.stakeMax,
    stakeMode: agent.stakeMode ?? 'RANGE',
    stakeAverageMultiplier: agent.stakeAverageMultiplier ?? 1,
    stakeBalanceMaxPercent: agent.stakeBalanceMaxPercent ?? 0,
    stakeRoundingStep: agent.stakeRoundingStep ?? 0,
    invitePurchasersOnly: agent.invitePurchasersOnly ?? false,
    sportIds: agent.sportIds,
    betTypeGroupIds: agent.betTypeGroupIds,
    varietyParamPValues: agent.varietyParamPValues,
    matchPopularityCriteria: agent.matchPopularityCriteria ?? [],
    matchMinBets: agent.matchMinBets ?? 0,
    matchMinViews: agent.matchMinViews ?? 0,
    matchMinInterest: agent.matchMinInterest ?? 0,
    matchSortBy: agent.matchSortBy ?? 'RANDOM',
    oddsMin: agent.oddsMin,
    oddsMax: agent.oddsMax,
    oddsStrategy: agent.oddsStrategy,
    hoursAhead: agent.hoursAhead,
    includeLive: agent.includeLive,
    maxPerHour: agent.maxPerHour,
    maxPerDay: agent.maxPerDay,
    maxActiveBattles: agent.maxActiveBattles ?? 0,
    maxActiveVisibility:
        agent.maxActiveVisibility === 'PUBLIC' || agent.maxActiveVisibility === 'PRIVATE'
            ? agent.maxActiveVisibility
            : 'BOTH',
    intervalSeconds: agent.intervalSeconds ?? 0,
    cooldownMinutesPerCreator: agent.cooldownMinutesPerCreator,
    minCreatorBalance: agent.minCreatorBalance,
    inviteTagSlugs: agent.inviteTagSlugs,
    inviteTagMode: agent.inviteTagMode,
    inviteExcludeTagSlugs: agent.inviteExcludeTagSlugs,
    inviteExcludeUserIds: agent.inviteExcludeUserIds,
    inviteUserIds: agent.inviteUserIds ?? [],
    maxInvitesPerTargetDays: agent.maxInvitesPerTargetDays,
    invitePairCooldownHours: agent.invitePairCooldownHours ?? 24,
    creatorSort: agent.creatorSort,
    notes: agent.notes,
});

export default function BattleAgentEditPage() {
    const { id } = useParams();
    const isNew = !id || id === 'new';
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(!isNew);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [form, setForm] = React.useState<BattleAgentUpsertRequest>(emptyBattleAgentForm());
    const [tags, setTags] = React.useState<UserTagDefinitionDto[]>([]);
    const [options, setOptions] = React.useState<CompetitionOptions | null>(null);
    const [runs, setRuns] = React.useState<BattleAgentRunDto[]>([]);
    const [runsPage, setRunsPage] = React.useState(0);
    const [hasMoreRuns, setHasMoreRuns] = React.useState(false);
    const [loadingMoreRuns, setLoadingMoreRuns] = React.useState(false);
    const [botPool, setBotPool] = React.useState<CandidateUser[]>([]);
    const [showAdvancedCreatorSearch, setShowAdvancedCreatorSearch] = React.useState(false);
    const [creatorSearch, setCreatorSearch] = React.useState('');
    const [inactiveDaysMin, setInactiveDaysMin] = React.useState<number>(7);
    const [creatorTagFilter, setCreatorTagFilter] = React.useState<string[]>([]);
    const [candidates, setCandidates] = React.useState<CandidateUser[]>([]);
    const [selectedCreators, setSelectedCreators] = React.useState<CandidateUser[]>([]);
    const [searching, setSearching] = React.useState(false);
    const [manualBotId, setManualBotId] = React.useState('');

    React.useEffect(() => {
        (async () => {
            try {
                const [tagList, competitionOptions, botsResponse] = await Promise.all([
                    getUserTags().catch(() => null),
                    getCompetitionOptions().catch(() => null),
                    getBattleAgentBotAccounts().catch(() => null),
                ]);
                setTags((tagList as UserTagDefinitionDto[]) ?? []);
                if (competitionOptions) {
                    setOptions(competitionOptions);
                }
                const pool: CandidateUser[] = (botsResponse?.items ?? []).map((b: BattleAgentBotAccountDto) => ({
                    id: b.userId,
                    name: b.name,
                    email: b.email,
                    tags: [],
                    lastActiveAt: b.lastVisitAt,
                    moneyBalance: b.moneyBalance,
                }));
                setBotPool(pool);

                if (!isNew && id) {
                    const agent = await getBattleAgent(id);
                    if (!agent) {
                        setError('Agent not found');
                        setLoading(false);
                        return;
                    }
                    setForm(toUpsert(agent));
                    const byId = new Map(pool.map(b => [b.id, b]));
                    setSelectedCreators(agent.creatorUserIds.map(uid => byId.get(uid) ?? ({
                        id: uid,
                        name: `User #${uid}`,
                        email: null,
                        tags: [],
                        lastActiveAt: null,
                        moneyBalance: null,
                    })));
                    const runList = await getBattleAgentRuns(id, 0, 50);
                    setRuns(runList ?? []);
                    setRunsPage(0);
                    setHasMoreRuns((runList?.length ?? 0) >= 50);
                }
            } catch (e) {
                setError(e instanceof Error ? e.message : 'Failed to load agent editor');
            } finally {
                setLoading(false);
            }
        })();
    }, [id, isNew]);

    const patch = <K extends keyof BattleAgentUpsertRequest>(key: K, value: BattleAgentUpsertRequest[K]) => {
        setForm(prev => ({ ...prev, [key]: value }));
    };

    const toggleMode = (mode: BattleAgentMode) => {
        setForm(prev => {
            const has = prev.modes.includes(mode);
            const modes = has ? prev.modes.filter(m => m !== mode) : [...prev.modes, mode];
            return { ...prev, modes: modes.length ? modes : prev.modes };
        });
    };

    const searchCreators = async () => {
        setSearching(true);
        setError(null);
        const result = await searchBattleAgentCreators({
            searchTerm: creatorSearch || undefined,
            userTags: creatorTagFilter.length ? creatorTagFilter : undefined,
            tagCombinationMode: 'ANY',
            inactiveDaysMin: inactiveDaysMin || undefined,
            sort: form.creatorSort,
            limit: 40,
        });
        if (!result) {
            setCandidates([]);
            setError('Failed to search creator candidates.');
        } else {
            setCandidates(result.items.map(u => ({
                id: u.id,
                name: u.name,
                email: u.email,
                tags: u.tags,
                lastActiveAt: u.lastActiveAt,
                moneyBalance: u.moneyBalance,
            })));
        }
        setSearching(false);
    };

    const addCreator = (user: CandidateUser) => {
        if (selectedCreators.some(c => c.id === user.id)) return;
        const next = [...selectedCreators, user];
        setSelectedCreators(next);
        patch('creatorUserIds', next.map(c => c.id));
    };

    const setCreatorsFromPool = (users: CandidateUser[]) => {
        setSelectedCreators(users);
        patch('creatorUserIds', users.map(c => c.id));
    };

    const addManualBotId = () => {
        const userId = Number(manualBotId);
        if (!Number.isFinite(userId) || userId <= 0) {
            setError('Enter a valid bot user id.');
            return;
        }
        const fromPool = botPool.find(b => b.id === userId);
        addCreator(fromPool ?? {
            id: userId,
            name: `User #${userId}`,
            email: null,
            tags: [],
            lastActiveAt: null,
            moneyBalance: null,
        });
        setManualBotId('');
    };

    const removeCreator = (userId: number) => {
        const next = selectedCreators.filter(c => c.id !== userId);
        setSelectedCreators(next);
        patch('creatorUserIds', next.map(c => c.id));
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        if (!form.name.trim()) {
            setSaving(false);
            setError('Name is required.');
            return;
        }
        if (selectedCreators.length === 0) {
            setSaving(false);
            setError('Add at least one creator (bot account).');
            return;
        }
        if (form.modes.length === 0) {
            setSaving(false);
            setError('Select at least one battle mode (Public and/or Private).');
            return;
        }
        if (form.modes.includes('PRIVATE') && !form.invitePurchasersOnly && form.inviteTagSlugs.length === 0) {
            setSaving(false);
            setError('Private mode needs invite tags or “Invite purchasers only”.');
            return;
        }
        if (form.stakeMin < 500 || form.stakeMax < form.stakeMin) {
            setSaving(false);
            setError('Stake min must be ≥ 500 and ≤ stake max.');
            return;
        }
        if (form.stakeRoundingStep < 0) {
            setSaving(false);
            setError('Round stake step must be ≥ 0 (0 = no rounding).');
            return;
        }
        if (form.stakeRoundingStep > 0) {
            const min = Math.max(500, form.stakeMin);
            const max = Math.max(min, form.stakeMax);
            const step = form.stakeRoundingStep;
            const firstMultiple = Math.ceil(min / step) * step;
            if (firstMultiple > max) {
                setSaving(false);
                setError('Stake range must contain at least one multiple of the rounding step.');
                return;
            }
        }
        const payload: BattleAgentUpsertRequest = {
            ...form,
            creatorUserIds: selectedCreators.map(c => c.id),
            slug: form.slug || form.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-'),
            stakeMode: form.modes.includes('PRIVATE') ? form.stakeMode : 'RANGE',
        };
        const result = isNew
            ? await createBattleAgent(payload)
            : await updateBattleAgent(id!, payload);
        setSaving(false);
        if (!result.ok) {
            setError(result.error);
            return;
        }
        const inviteCount = result.data.inviteUserIds?.length ?? 0;
        const usedTags = payload.modes.includes('PRIVATE') && payload.inviteTagSlugs.length > 0;
        let info = 'Agent saved.';
        if (payload.modes.includes('PRIVATE')) {
            if (usedTags) {
                info += ` Invite pool resolved: ${inviteCount} user(s). Analytics refreshes this pool when the agent create interval elapses and/or every 50 successful creates.`;
                if (inviteCount >= 100) {
                    info += ' Warning: tag resolution is capped at 100 — narrow tags if needed.';
                }
            } else if (payload.invitePurchasersOnly && inviteCount === 0) {
                info += ' Purchasers-only: invitees are chosen live from recent purchasers at runtime.';
            } else if (inviteCount > 0) {
                info += ` Manual invite list: ${inviteCount} user(s).`;
            }
        }
        window.alert(info);
        navigate('/battle-agents');
    };

    const creatorOptions = React.useMemo(() => {
        const map = new Map(botPool.map(b => [b.id, b]));
        selectedCreators.forEach(c => {
            if (!map.has(c.id)) map.set(c.id, c);
        });
        return Array.from(map.values());
    }, [botPool, selectedCreators]);

    if (loading) {
        return <Box sx={{ m: 3 }}><CircularProgress /></Box>;
    }

    const tagOptions = tags.map(t => t.tagName);
    const sports = options?.sports ?? [];
    const betGroups = options?.betGroups ?? [];
    const bothModes = form.modes.includes('PUBLIC') && form.modes.includes('PRIVATE');

    return (
        <Box sx={{ mx: 3, my: 3, maxWidth: 1100 }}>
            <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h4">{isNew ? 'New battle agent' : 'Edit battle agent'}</Typography>
                    <Button onClick={() => navigate('/battle-agents')}>Back</Button>
                </Stack>
                {error && <Alert severity="error">{error}</Alert>}

                <Stack spacing={2}>
                    <Typography variant="h6">General</Typography>
                    <TextField label="Name" value={form.name} onChange={e => patch('name', e.target.value)} fullWidth />
                    <TextField label="Slug" value={form.slug} onChange={e => patch('slug', e.target.value)} fullWidth helperText="Unique id, e.g. dormant-public-ro" />
                    <FormControlLabel control={<Switch checked={form.enabled} onChange={e => patch('enabled', e.target.checked)} />} label="Enabled" />
                    <TextField label="Notes" value={form.notes ?? ''} onChange={e => patch('notes', e.target.value || null)} fullWidth multiline minRows={2} />
                </Stack>

                <Stack spacing={2}>
                    <Typography variant="h6">Battle modes</Typography>
                    <FormGroup row>
                        <FormControlLabel control={<Checkbox checked={form.modes.includes('PUBLIC')} onChange={() => toggleMode('PUBLIC')} />} label="Public" />
                        <FormControlLabel control={<Checkbox checked={form.modes.includes('PRIVATE')} onChange={() => toggleMode('PRIVATE')} />} label="Private" />
                    </FormGroup>
                    {bothModes && (
                        <TextField
                            type="number"
                            label="Public share (%)"
                            value={Math.round(form.publicShare * 100)}
                            onChange={e => patch('publicShare', Math.min(100, Math.max(0, Number(e.target.value))) / 100)}
                            helperText={`Private share ≈ ${Math.round((1 - form.publicShare) * 100)}%`}
                            inputProps={{ min: 0, max: 100, step: 5 }}
                            sx={{ maxWidth: 280 }}
                        />
                    )}
                </Stack>

                <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1}>
                        <Typography variant="h6">Creators (bot accounts)</Typography>
                        <Button size="small" onClick={() => navigate('/battle-agents/bots')}>
                            Manage bots
                        </Button>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        Pick bots from the Bot accounts pool. Manage balance / profile there, then select them here for this agent.
                    </Typography>
                    <Autocomplete
                        multiple
                        options={creatorOptions}
                        value={selectedCreators}
                        onChange={(_, value) => setCreatorsFromPool(value)}
                        getOptionLabel={u => `${u.name ?? 'User'} (#${u.id})${u.moneyBalance != null ? ` · ${Math.round(u.moneyBalance)}` : ''}`}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        filterSelectedOptions
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                {option.name ?? 'User'} (#{option.id})
                                {option.moneyBalance != null ? ` · bal ${Math.round(option.moneyBalance)}` : ''}
                            </li>
                        )}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Select bots from pool"
                                helperText={botPool.length === 0
                                    ? 'Pool empty — add bots on Bot accounts page, or enter a user id below.'
                                    : `${botPool.length} bots available`}
                            />
                        )}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                        <TextField
                            size="small"
                            label="Or add user id"
                            value={manualBotId}
                            onChange={e => setManualBotId(e.target.value)}
                            sx={{ minWidth: 180 }}
                        />
                        <Button variant="outlined" onClick={addManualBotId}>Add id</Button>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Creator sort</InputLabel>
                            <Select
                                label="Creator sort"
                                value={form.creatorSort}
                                onChange={e => patch('creatorSort', e.target.value as BattleAgentCreatorSort)}
                            >
                                {SORT_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {selectedCreators.map(c => (
                            <Chip
                                key={c.id}
                                label={`${c.name ?? c.id} (#${c.id})`}
                                onDelete={() => removeCreator(c.id)}
                            />
                        ))}
                    </Stack>

                    <Button size="small" onClick={() => setShowAdvancedCreatorSearch(v => !v)}>
                        {showAdvancedCreatorSearch ? 'Hide user search' : 'Find more users (tag search)'}
                    </Button>
                    {showAdvancedCreatorSearch && (
                        <Stack spacing={2}>
                            <Typography variant="body2" color="text.secondary">
                                Advanced: search any user (e.g. dormant accounts) and add them as bots for this agent.
                            </Typography>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <TextField label="Search name/email" value={creatorSearch} onChange={e => setCreatorSearch(e.target.value)} fullWidth />
                                <TextField type="number" label="Inactive days min" value={inactiveDaysMin} onChange={e => setInactiveDaysMin(Number(e.target.value))} sx={{ minWidth: 160 }} />
                            </Stack>
                            <Autocomplete
                                multiple
                                options={tagOptions}
                                value={creatorTagFilter}
                                onChange={(_, value) => setCreatorTagFilter(value)}
                                renderInput={params => <TextField {...params} label="Filter by user tags" />}
                            />
                            <Button variant="outlined" onClick={searchCreators} disabled={searching}>
                                {searching ? 'Searching…' : 'Search candidates'}
                            </Button>
                            {candidates.length > 0 && (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>User</TableCell>
                                            <TableCell>Last active</TableCell>
                                            <TableCell>Balance</TableCell>
                                            <TableCell>Tags</TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {candidates.map(c => (
                                            <TableRow key={c.id}>
                                                <TableCell>{c.name ?? '—'} ({c.id})</TableCell>
                                                <TableCell>{c.lastActiveAt ? new Date(c.lastActiveAt).toLocaleString() : '—'}</TableCell>
                                                <TableCell>{c.moneyBalance ?? '—'}</TableCell>
                                                <TableCell>{c.tags.join(', ') || '—'}</TableCell>
                                                <TableCell>
                                                    <Button size="small" onClick={() => addCreator(c)}>Add</Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Stack>
                    )}
                </Stack>

                <Stack spacing={2}>
                    <Typography variant="h6">Match & bet rules</Typography>
                    <Autocomplete
                        multiple
                        options={sports}
                        getOptionLabel={s => s.name}
                        value={sports.filter(s => form.sportIds.includes(s.id))}
                        onChange={(_, value) => patch('sportIds', value.map(s => s.id))}
                        renderInput={params => <TextField {...params} label="Sports (empty = any)" />}
                    />
                    <Autocomplete
                        multiple
                        options={betGroups}
                        getOptionLabel={g => g.name}
                        value={betGroups.filter(g => form.betTypeGroupIds.includes(g.id))}
                        onChange={(_, value) => patch('betTypeGroupIds', value.map(g => g.id))}
                        renderInput={params => <TextField {...params} label="Bet type groups (empty = any)" />}
                    />
                    <TextField
                        label="Variety paramP values (comma-separated, e.g. 0 for full match)"
                        value={form.varietyParamPValues.join(',')}
                        onChange={e => {
                            const values = e.target.value.split(',').map(v => v.trim()).filter(Boolean).map(Number).filter(n => !Number.isNaN(n));
                            patch('varietyParamPValues', values);
                        }}
                        helperText="Optional. 0 = full game in most sports."
                    />
                    <Typography variant="subtitle2">Match popularity (combinable)</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Prefer matches by bets placed, views, and/or editorial interest. Selected criteria are ANDed for mins; ranking uses Sort by.
                    </Typography>
                    <FormGroup row>
                        {MATCH_POPULARITY_OPTIONS.map(opt => (
                            <FormControlLabel
                                key={opt.value}
                                control={
                                    <Checkbox
                                        checked={form.matchPopularityCriteria.includes(opt.value)}
                                        onChange={e => {
                                            const next = e.target.checked
                                                ? [...form.matchPopularityCriteria, opt.value]
                                                : form.matchPopularityCriteria.filter(c => c !== opt.value);
                                            patch('matchPopularityCriteria', next);
                                        }}
                                    />
                                }
                                label={opt.label}
                            />
                        ))}
                    </FormGroup>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                            type="number"
                            label="Min bets"
                            disabled={!form.matchPopularityCriteria.includes('BETS')}
                            value={form.matchMinBets}
                            onChange={e => patch('matchMinBets', Number(e.target.value))}
                            helperText="Only if Bets is checked"
                        />
                        <TextField
                            type="number"
                            label="Min views"
                            disabled={!form.matchPopularityCriteria.includes('VIEWS')}
                            value={form.matchMinViews}
                            onChange={e => patch('matchMinViews', Number(e.target.value))}
                            helperText="Only if Views is checked"
                        />
                        <TextField
                            type="number"
                            label="Min interest"
                            disabled={!form.matchPopularityCriteria.includes('INTEREST')}
                            value={form.matchMinInterest}
                            onChange={e => patch('matchMinInterest', Number(e.target.value))}
                            helperText="League + home + away factors"
                        />
                        <FormControl sx={{ minWidth: 180 }}>
                            <InputLabel>Rank matches by</InputLabel>
                            <Select
                                label="Rank matches by"
                                value={form.matchSortBy}
                                onChange={e => patch('matchSortBy', e.target.value as BattleAgentMatchSortBy)}
                            >
                                {MATCH_SORT_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <FormControl sx={{ minWidth: 220 }}>
                            <InputLabel>Stake mode</InputLabel>
                            <Select
                                label="Stake mode"
                                value={form.modes.includes('PRIVATE') ? form.stakeMode : 'RANGE'}
                                disabled={!form.modes.includes('PRIVATE')}
                                onChange={e => patch('stakeMode', e.target.value as BattleAgentStakeMode)}
                            >
                                {STAKE_MODES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField type="number" label="Stake min (clamp, ≥500)" value={form.stakeMin} onChange={e => patch('stakeMin', Math.max(500, Number(e.target.value)))} />
                        <TextField type="number" label="Stake max (clamp)" value={form.stakeMax} onChange={e => patch('stakeMax', Math.max(500, Number(e.target.value)))} />
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Round stake to</InputLabel>
                            <Select
                                label="Round stake to"
                                value={form.stakeRoundingStep}
                                onChange={e => patch('stakeRoundingStep', Number(e.target.value))}
                            >
                                {STAKE_ROUNDING_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    {form.modes.includes('PRIVATE') && form.stakeMode === 'TARGET_AVERAGE' && (
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                                type="number"
                                label="Avg bet multiplier"
                                value={form.stakeAverageMultiplier}
                                onChange={e => patch('stakeAverageMultiplier', Number(e.target.value))}
                                helperText="Stake ≈ opponent averageBetAmount × multiplier"
                            />
                            <TextField
                                type="number"
                                label="Max % of opponent balance"
                                value={form.stakeBalanceMaxPercent}
                                onChange={e => patch('stakeBalanceMaxPercent', Number(e.target.value))}
                                helperText="0 = ignore balance cap"
                            />
                        </Stack>
                    )}
                    <Typography variant="body2" color="text.secondary">
                        RANGE = random between min/max.
                        {form.modes.includes('PRIVATE')
                            ? ' TARGET_AVERAGE = opponent usual bet size (private battles only); min/max still clamp.'
                            : ' TARGET_AVERAGE is only available when Private mode is enabled.'}
                        {' '}Rounding applies to all placed stakes (public and private). Ties round up (e.g. 1750 with step 500 → 2000).
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField type="number" label="Odds min" value={form.oddsMin ?? ''} onChange={e => patch('oddsMin', e.target.value === '' ? null : Number(e.target.value))} />
                        <TextField type="number" label="Odds max" value={form.oddsMax ?? ''} onChange={e => patch('oddsMax', e.target.value === '' ? null : Number(e.target.value))} />
                    </Stack>
                    <FormControl sx={{ maxWidth: 280 }}>
                        <InputLabel>Odds strategy</InputLabel>
                        <Select label="Odds strategy" value={form.oddsStrategy} onChange={e => patch('oddsStrategy', e.target.value as BattleAgentOddsStrategy)}>
                            {ODDS_STRATEGIES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField type="number" label="Hours ahead" value={form.hoursAhead} onChange={e => patch('hoursAhead', Number(e.target.value))} />
                        <FormControlLabel control={<Switch checked={form.includeLive} onChange={e => patch('includeLive', e.target.checked)} />} label="Include live matches" />
                    </Stack>
                </Stack>

                <Stack spacing={2}>
                    <Typography variant="h6">Schedule & guardrails</Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                            type="number"
                            label="Create every (seconds)"
                            value={form.intervalSeconds}
                            onChange={e => patch('intervalSeconds', Math.max(0, Number(e.target.value)))}
                            helperText="0 = use Global settings interval. Min effective 10s when set."
                            sx={{ minWidth: 220 }}
                        />
                        <TextField type="number" label="Max / hour" value={form.maxPerHour} onChange={e => patch('maxPerHour', Number(e.target.value))} />
                        <TextField type="number" label="Max / day" value={form.maxPerDay} onChange={e => patch('maxPerDay', Number(e.target.value))} />
                        <TextField type="number" label="Creator cooldown (min)" value={form.cooldownMinutesPerCreator} onChange={e => patch('cooldownMinutesPerCreator', Number(e.target.value))} />
                        <TextField
                            type="number"
                            label="Min creator balance"
                            value={form.minCreatorBalance}
                            onChange={e => patch('minCreatorBalance', Number(e.target.value))}
                            helperText="Skip creators below this balance"
                        />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
                        <TextField
                            type="number"
                            label="Max active battles (this agent)"
                            value={form.maxActiveBattles}
                            onChange={e => patch('maxActiveBattles', Math.max(0, Number(e.target.value)))}
                            helperText="0 = unlimited. Counts PENDING/ACCEPTED/STARTED created by this agent's bots. Global cap still applies."
                            sx={{ minWidth: 260 }}
                        />
                        <FormControl sx={{ minWidth: 260 }}>
                            <InputLabel>Active limit counts</InputLabel>
                            <Select
                                label="Active limit counts"
                                value={form.maxActiveVisibility}
                                onChange={e => patch('maxActiveVisibility', e.target.value as BattleAgentMaxActiveVisibility)}
                            >
                                {MAX_ACTIVE_VISIBILITY_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Stack>

                {form.modes.includes('PRIVATE') && (
                    <Stack spacing={2}>
                        <Typography variant="h6">Private invite targets</Typography>
                        <Alert severity="info">
                            Saved invite pool: <strong>{form.inviteUserIds.length}</strong> user(s).
                            {form.inviteTagSlugs.length > 0
                                ? ` Tags resolve into a saved invite pool on Save. Analytics refreshes that pool when the agent create interval (Create every / seconds) elapses and/or every 50 successful creates. Current pool: ${form.inviteUserIds.length} user(s). Crons picks randomly from that pool.`
                                : form.invitePurchasersOnly
                                    ? ' Purchasers-only without tags: invitees are chosen live at runtime from recent purchasers.'
                                    : ' Add tags (or enable purchasers-only) so the agent has people to invite.'}
                            {form.inviteUserIds.length >= 100 ? ' Cap reached (100) — narrow tags if needed.' : ''}
                        </Alert>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.invitePurchasersOnly}
                                    onChange={e => patch('invitePurchasersOnly', e.target.checked)}
                                />
                            }
                            label="Invite purchasers only (users with at least one purchase)"
                        />
                        <Autocomplete
                            multiple
                            options={tagOptions}
                            value={form.inviteTagSlugs}
                            onChange={(_, value) => patch('inviteTagSlugs', value)}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label={form.invitePurchasersOnly ? 'Invite user tags (optional filter)' : 'Invite user tags (required)'}
                                />
                            )}
                        />
                        <FormControl sx={{ maxWidth: 200 }}>
                            <InputLabel>Tag mode</InputLabel>
                            <Select label="Tag mode" value={form.inviteTagMode} onChange={e => patch('inviteTagMode', e.target.value as BattleAgentInviteTagMode)}>
                                <MenuItem value="ANY">ANY</MenuItem>
                                <MenuItem value="ALL">ALL</MenuItem>
                            </Select>
                        </FormControl>
                        <Autocomplete
                            multiple
                            options={tagOptions}
                            value={form.inviteExcludeTagSlugs}
                            onChange={(_, value) => patch('inviteExcludeTagSlugs', value)}
                            renderInput={params => <TextField {...params} label="Exclude tags" />}
                        />
                        <TextField
                            label="Exclude user IDs (comma-separated)"
                            value={form.inviteExcludeUserIds.join(',')}
                            onChange={e => {
                                const values = e.target.value.split(',').map(v => v.trim()).filter(Boolean).map(Number).filter(n => !Number.isNaN(n));
                                patch('inviteExcludeUserIds', values);
                            }}
                        />
                        <TextField
                            type="number"
                            label="Same bot → same user cooldown (hours)"
                            value={form.invitePairCooldownHours}
                            onChange={e => patch('invitePairCooldownHours', Math.max(0, Number(e.target.value)))}
                            helperText="0 = off. Prevents the same creator bot from inviting the same user again within N hours."
                        />
                        <TextField
                            type="number"
                            label="Any bot → same user cooldown (days)"
                            value={form.maxInvitesPerTargetDays}
                            onChange={e => patch('maxInvitesPerTargetDays', Math.max(0, Number(e.target.value)))}
                            helperText="0 = off. Skip users any creator on this agent already invited successfully within N days."
                        />
                    </Stack>
                )}

                {!isNew && (
                    <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Recent runs</Typography>
                            <Button
                                size="small"
                                disabled={loadingMoreRuns || !hasMoreRuns}
                                onClick={async () => {
                                    if (!id) return;
                                    setLoadingMoreRuns(true);
                                    const nextOffset = (runsPage + 1) * 50;
                                    const next = await getBattleAgentRuns(id, nextOffset, 50);
                                    if (!next) {
                                        setError('Failed to load more runs.');
                                        setHasMoreRuns(false);
                                    } else {
                                        setRuns(prev => [...prev, ...next]);
                                        setRunsPage(prev => prev + 1);
                                        setHasMoreRuns(next.length >= 50);
                                    }
                                    setLoadingMoreRuns(false);
                                }}
                            >
                                {loadingMoreRuns ? 'Loading…' : hasMoreRuns ? 'Load more' : 'No more runs'}
                            </Button>
                        </Stack>
                        {runs.length === 0 && <Typography color="text.secondary">No runs yet.</Typography>}
                        {runs.length > 0 && (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>When</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Mode</TableCell>
                                        <TableCell>Creator</TableCell>
                                        <TableCell>Invitee</TableCell>
                                        <TableCell>Stake</TableCell>
                                        <TableCell>Match</TableCell>
                                        <TableCell>Battle</TableCell>
                                        <TableCell>Message</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {runs.map(run => (
                                        <TableRow key={run.id}>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {new Date(run.createdAt).toLocaleString()}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={run.status}
                                                    color={
                                                        run.status === 'SUCCESS' ? 'success'
                                                            : run.status === 'FAILED' ? 'error'
                                                                : 'default'
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>{run.mode ?? '—'}</TableCell>
                                            <TableCell>
                                                {run.creatorUserId != null ? (
                                                    <Button size="small" onClick={() => navigate('/users/' + run.creatorUserId)}>
                                                        #{run.creatorUserId}
                                                    </Button>
                                                ) : '—'}
                                            </TableCell>
                                            <TableCell>
                                                {run.inviteeUserId != null ? (
                                                    <Button size="small" onClick={() => navigate('/users/' + run.inviteeUserId)}>
                                                        #{run.inviteeUserId}
                                                    </Button>
                                                ) : '—'}
                                            </TableCell>
                                            <TableCell>{run.moneyPlaced ?? '—'}</TableCell>
                                            <TableCell>
                                                {run.matchId != null ? (
                                                    <Button size="small" onClick={() => navigate('/matches/' + run.matchId)}>
                                                        #{run.matchId}
                                                    </Button>
                                                ) : '—'}
                                            </TableCell>
                                            <TableCell>
                                                {run.challengeId != null ? (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => navigate('/battles/' + run.challengeId)}
                                                    >
                                                        Open #{run.challengeId}
                                                    </Button>
                                                ) : '—'}
                                            </TableCell>
                                            <TableCell>{run.message ?? '—'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Stack>
                )}

                <Stack direction="row" spacing={2}>
                    <Button variant="contained" onClick={save} disabled={saving}>
                        {saving ? 'Saving…' : 'Save agent'}
                    </Button>
                    <Button onClick={() => navigate('/battle-agents')}>Cancel</Button>
                </Stack>
            </Stack>
        </Box>
    );
}
